import { useCallback } from 'react'
import { useSelector } from '@xstate/react'
import { generatePath } from 'react-router-dom'
import { machine } from './machine'
import { InformalFeedbackRequest } from 'api/clients/InformalFeedbackRequestsClient'
import { AlmanacFile, ArticleFlat } from 'types'
import { DOC_PATH } from 'paths'
import { useSourceData } from './SourceDataProvider/useSourceData'
import { SubmitVariables, useSubmit } from './useSubmit'
import { ReviewsWizardProvider } from './ReviewsWizardProvider'
import { SourceDataProvider } from './SourceDataProvider/SourceDataProvider'
import { ReviewsWizardModal } from './ReviewsWizardModal'
import { useIsNotSelf } from 'hooks/useIsNotSelf'
import { catchBlockedPopUp } from 'utils/catchBlockedPopUp'
import { useInterpretReviewMachine } from './useInterpretReviewMachine'
import { ReviewAssetType } from './types'
import { useCreateScheduledReview } from './useCreateScheduledReview'
import { showToastMessage } from '../../common/ToastMessage/ShowToastMessage'

interface Props {
  onGoBack?: () => void
  overrideFolder?: AlmanacFile | null
}

export const ReviewsAnythingWizard = ({ overrideFolder }: Props) => {
  const handleError = useCallback((error: any) => {
    showToastMessage(error.message, 'error')
  }, [])
  const createScheduledReview = useCreateScheduledReview({ onError: handleError })

  const handleCreateRequestSuccess = useCallback(
    async (request: InformalFeedbackRequest, submitVariables: SubmitVariables) => {
      createScheduledReview(request, submitVariables)
      // TODO: close all modals on the stack

      setTimeout(() => {
        catchBlockedPopUp(generatePath(DOC_PATH, { slug: request.article.slug }))
      }, 250)
    },
    [createScheduledReview],
  )

  const { submit, isLoading } = useSubmit({ canBePrivate: false }, { onSuccess: handleCreateRequestSuccess })
  const isNotSelf = useIsNotSelf()

  const reviewMachine = machine({
    onSubmit: submit,
    canChangePrivacy: false,
    isNotSelf,
  })
  const service = useInterpretReviewMachine(reviewMachine)

  const asset = useSelector(service, (state) => state.context.asset)
  const assetType = useSelector(service, (state) => state.context.assetType)
  const sourceData = useSourceData(assetType === ReviewAssetType.Article ? { article: asset as ArticleFlat } : {})

  return (
    <ReviewsWizardProvider canChangePrivacy={false} isLoading={isLoading} service={service} canRecur>
      <SourceDataProvider sourceData={sourceData}>
        <ReviewsWizardModal overrideFolder={overrideFolder} />
      </SourceDataProvider>
    </ReviewsWizardProvider>
  )
}
